import { useEffect, useState, useCallback, type FormEvent, type KeyboardEvent } from "react";
import { t } from "../i18n";
import { isValidInstagramHandle, normalizeInstagramHandle } from "@lib/utils";

interface HandleSearchInputProps {
  onSearch: (handle: string) => void;
  isLoading: boolean;
  placeholder?: string;
  buttonLabel?: string;
  autoFocus?: boolean;
  large?: boolean;
  initialValue?: string;
}

export function HandleSearchInput({
  onSearch,
  isLoading,
  placeholder,
  buttonLabel,
  autoFocus = false,
  large = false,
  initialValue = "",
}: HandleSearchInputProps) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(initialValue);
    setError(null);
  }, [initialValue]);

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      setError(null);

      const trimmed = value.trim();
      if (trimmed.length === 0) {
        setError(t("searchErrorEmpty"));
        return;
      }

      const normalized = normalizeInstagramHandle(trimmed);
      if (normalized.length === 0) {
        setError(t("searchErrorEmpty"));
        return;
      }

      if (!isValidInstagramHandle(normalized)) {
        setError(t("searchErrorInvalid"));
        return;
      }

      onSearch(normalized);
    },
    [value, onSearch],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const inputClasses = large
    ? "w-full px-5 py-4 text-lg rounded-xl border-2 border-gray-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all"
    : "w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all";

  return (
    <form onSubmit={handleSubmit} className="w-full handle-search-form">
      <div className="flex gap-2 handle-search-row">
        <div className="flex-1 relative handle-search-field">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none handle-search-prefix">
            @
          </span>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? t("landingHeroPlaceholder")}
            autoFocus={autoFocus}
            disabled={isLoading}
            className={`${inputClasses} ${large ? "pl-8" : "pl-7"} handle-search-input`}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`shrink-0 inline-flex items-center justify-center font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors handle-search-submit ${
            large ? "px-8 py-4 text-lg rounded-xl" : "px-5 py-3 text-sm rounded-lg"
          }`}
        >
          {isLoading ? (
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            buttonLabel ?? t("landingHeroCta")
          )}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-500 handle-search-error">{error}</p>
      )}
    </form>
  );
}
